/*!
 * SkyPlus Profile Component
 * Location: alfresco-content-app/app/src/app/components/skyplus/profile/skyplus-profile.component.ts
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { PeopleContentService } from '@alfresco/adf-content-services';

interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  location: string;
  telephone: string;
  mobile: string;
  company: string;
  companyAddress1: string;
  companyAddress2: string;
  companyAddress3: string;
  companyPostcode: string;
  companyTelephone: string;
  companyFax: string;
  companyEmail: string;
}

@Component({
  selector: 'sp-profile',
  templateUrl: './skyplus-profile.component.html',
  styleUrls: ['./skyplus-profile.component.scss']
})
export class SkyPlusProfileComponent implements OnInit, OnDestroy {
  
  // Profile state
  isLoading = false;
  isSaving = false;
  
  // Section states
  generalExpanded = true;
  companyExpanded = false;
  
  // Edit modes
  editingGeneral = false;
  editingCompany = false;
  
  // User profile data
  profile: UserProfile = {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    location: '',
    telephone: '',
    mobile: '',
    company: '',
    companyAddress1: '',
    companyAddress2: '',
    companyAddress3: '',
    companyPostcode: '',
    companyTelephone: '',
    companyFax: '',
    companyEmail: ''
  };
  
  // Backup for cancel
  profileBackup: UserProfile | null = null;
  
  private destroy$ = new Subject<void>();
  
  constructor(
    public router: Router,
    private peopleService: PeopleContentService
  ) {}
  
  ngOnInit(): void {
    this.loadProfile();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  // ===== PROFILE METHODS =====
  
  async loadProfile(): Promise<void> {
    this.isLoading = true;
    
    try {
      const person = await this.peopleService.getCurrentUserInfo().toPromise();
      
      this.profile = {
        id: person.id || '',
        firstName: person.firstName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        jobTitle: person.jobTitle || '',
        location: person.location || '',
        telephone: person.telephone || '',
        mobile: person.mobile || '',
        company: person.company?.organization || '',
        companyAddress1: person.company?.address1 || '',
        companyAddress2: person.company?.address2 || '',
        companyAddress3: person.company?.address3 || '',
        companyPostcode: person.company?.postcode || '',
        companyTelephone: person.company?.telephone || '',
        companyFax: person.company?.fax || '',
        companyEmail: person.company?.email || ''
      };
      
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      this.isLoading = false;
    }
  }
    
  // ===== SECTION TOGGLES =====
  
  toggleGeneralSection(): void {
    this.generalExpanded = !this.generalExpanded;
  }
  
  toggleCompanySection(): void {
    this.companyExpanded = !this.companyExpanded;
  }
  
  // ===== EDIT MODES =====
  
  editGeneral(): void {
    this.profileBackup = { ...this.profile };
    this.editingGeneral = true;
  }
  
  cancelGeneralEdit(): void {
    if (this.profileBackup) {
      this.profile = { ...this.profileBackup };
    }
    this.editingGeneral = false;
    this.profileBackup = null;
  }
  
  async saveGeneral(): Promise<void> {
    this.isSaving = true;
    
    try {
      await this.peopleService.updatePerson(this.profile.id, {
        firstName: this.profile.firstName,
        lastName: this.profile.lastName,
        jobTitle: this.profile.jobTitle,
        location: this.profile.location,
        telephone: this.profile.telephone,
        mobile: this.profile.mobile
      }).toPromise();
      
      this.editingGeneral = false;
      this.profileBackup = null;
      
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }
  
  editCompany(): void {
    this.profileBackup = { ...this.profile };
    this.editingCompany = true;
  }
  
  cancelCompanyEdit(): void {
    if (this.profileBackup) {
      this.profile = { ...this.profileBackup };
    }
    this.editingCompany = false;
    this.profileBackup = null;
  }
  
  async saveCompany(): Promise<void> {
    this.isSaving = true;
    
    try {
      await this.peopleService.updatePerson(this.profile.id, {
        company: {
          organization: this.profile.company,
          address1: this.profile.companyAddress1,
          address2: this.profile.companyAddress2,
          address3: this.profile.companyAddress3,
          postcode: this.profile.companyPostcode,
          telephone: this.profile.companyTelephone,
          fax: this.profile.companyFax,
          email: this.profile.companyEmail
        }
      }).toPromise();
      
      this.editingCompany = false;
      this.profileBackup = null;
      
    } catch (error) {
      console.error('Error saving company details:', error);
      alert('Error saving company details. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }
  
  // ===== NAVIGATION =====
  
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}